import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Settings } from 'lucide-react';

import ResourcesDrawer from '../../components/payroll/ResourcesDrawer';
import PaymentsTab from './tabs/PaymentsTab';
import FilingsTab from './tabs/FilingsTab';
import { usePD7A } from './usePD7A';
import { TOKENS, tabStyle } from './styles';
import type { PD7A, TabName } from './types';

const ResourcesDrawerAny = ResourcesDrawer as any;

export default function PayrollTaxes() {
  const navigate = useNavigate();
  const location = useLocation();

  const tabFromUrl: TabName = location.pathname.endsWith('/filings') ? 'filings' : 'payments';
  const [activeTab, setActiveTab] = useState<TabName>(tabFromUrl);
  const [pd7a, setPd7a] = useState<PD7A | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [resourcesOpen, setResourcesOpen] = useState<boolean>(false);

  const { data: pd7aData, isLoading: qLoading, error: qError } = usePD7A();

  // Sync react-query state into legacy local state (preserves pass-through behavior)
  useEffect(() => {
    if (pd7aData) setPd7a(pd7aData);
    setLoading(qLoading);
    if (qError) setError('Could not load remittance: ' + qError.message);
  }, [pd7aData, qLoading, qError]);

  const switchTab = (name: TabName): void => {
    setActiveTab(name);
    navigate('/payroll/taxes/' + name, { replace: true });
  };

  const openPrint = (): void => {
    window.open('/payroll/taxes/print?tab=' + activeTab, '_blank');
  };

  const hoverEnter = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.currentTarget.style.borderColor = TOKENS.teal;
    e.currentTarget.style.color = TOKENS.tealInk;
  };
  const hoverLeave = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.currentTarget.style.borderColor = TOKENS.ink;
    e.currentTarget.style.color = TOKENS.ink;
  };

  return (
    <div style={{
      background: TOKENS.bg,
      minHeight: '100vh',
      fontFamily: "Inter, 'Plus Jakarta Sans', sans-serif",
      color: TOKENS.ink,
      fontSize: 14,
      lineHeight: 1.5,
      fontWeight: 500,
    }}>
      <div style={{ maxWidth: '100%', margin: 0, padding: '28px 32px 90px' }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', marginBottom: 24, gap: 12,
        }}>
          <div>
            <h1 style={{
              fontSize: 34, fontWeight: 700, color: TOKENS.ink,
              letterSpacing: '-0.02em', margin: 0,
            }}>
              Payroll taxes
            </h1>
            <div style={{ fontSize: 14, color: TOKENS.ink, fontWeight: 500, marginTop: 4 }}>
              Track federal taxes, remittances, and filings, all in one place.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => navigate('/payroll/taxes/archived')}
              onMouseEnter={hoverEnter}
              onMouseLeave={hoverLeave}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: '#fff', color: TOKENS.ink,
                border: '1.5px solid ' + TOKENS.ink,
                borderRadius: 8, padding: '8px 14px',
                fontWeight: 700, fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit', transition: '0.12s',
              }}
            >
              <Calendar size={15} /> Tax calendar
            </button>
            <button
              onClick={() => navigate('/payroll/settings')}
              onMouseEnter={hoverEnter}
              onMouseLeave={hoverLeave}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: '#fff', color: TOKENS.ink,
                border: '1.5px solid ' + TOKENS.ink,
                borderRadius: 8, padding: '8px 14px',
                fontWeight: 700, fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit', transition: '0.12s',
              }}
            >
              <Settings size={15} /> Tax settings
            </button>
          </div>
        </div>

        <div style={{
          display: 'inline-flex',
          gap: 4,
          background: '#EDF0F3',
          borderRadius: 10,
          padding: 4,
          marginBottom: 22,
        }}>
          <button onClick={() => switchTab('payments')} style={tabStyle(activeTab === 'payments')}>
            Payments
          </button>
          <button onClick={() => switchTab('filings')} style={tabStyle(activeTab === 'filings')}>
            Filings
          </button>
        </div>

        {activeTab === 'payments' && (
          <PaymentsTab
            pd7a={pd7a}
            loading={loading}
            error={error}
            onResourcesOpen={() => setResourcesOpen(true)}
            onPrint={openPrint}
          />
        )}
        {activeTab === 'filings' && (
          <FilingsTab
            onResourcesOpen={() => setResourcesOpen(true)}
            onPrint={openPrint}
          />
        )}

        <ResourcesDrawerAny
          open={resourcesOpen}
          onClose={() => setResourcesOpen(false)}
          onNavigate={(path: string) => navigate(path)}
          country="CA"
        />
      </div>
    </div>
  );
}